import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { LuCheck } from 'react-icons/lu';

const StepperContainer = styled.div`
  width: 100%;
  padding: 30px 20px;
  padding-bottom: 0px;
  margin-bottom: 0px;

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const StepsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 968px) {
    padding: 0 10px;
  }
  
  @media (max-width: 768px) {
    padding: 0 5px;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
    
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }
`;

const ProgressLine = styled.div`
  position: absolute;
  top: 24px;
  left: 50px;
  right: 50px;
  height: 3px;
  background: ${props => props.theme.colors.border};
  z-index: 0;
  
  @media (max-width: 968px) {
    left: 30px;
    right: 30px;
  }
  
  @media (max-width: 768px) {
    left: 20px;
    right: 20px;
    top: 16px;
    height: 2px;
  }
`;

const ProgressLineFilled = styled.div`
  height: 100%;
  background: ${props => props.theme.colors.successGreen};
  transition: width 0.3s ease;
  width: ${props => props.$progress}%;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex: 1;
  z-index: 1;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  position: relative;
  min-width: fit-content;

  @media (max-width: 768px) {
    flex: 0 0 auto;
    min-width: 70px;
    padding: ${props => props.$active ? '5px' : '0'};
    background: ${props => props.$active ? props.theme.colors.lightBlue : 'transparent'};
    border-radius: 8px;
    gap: 8px;
  }
`;

const StepCircle = styled.div`
  min-width: 48px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => {
    if (props.$active) return props.theme.colors.secondary;
    if (props.$completed) return props.theme.colors.successGreen;
    return props.theme.colors.border;
  }};
  color: ${props => props.$active || props.$completed ? props.theme.colors.white : props.theme.colors.grayMedium};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  transition: background-color 0.3s ease, color 0.3s ease;
  position: relative;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-width: 32px;
    width: 32px;
    height: 32px;
    font-size: 14px;
    border: ${props => props.$active ? `2px solid ${props.theme.colors.secondary}` : 'none'};
  }
`;

const StepLabel = styled.div`
  text-align: center;
  font-size: 13px;
  line-height: 1.3;
  font-weight: ${props => props.$active ? '600' : '500'};
  color: ${props => {
    if (props.$active) return props.theme.colors.primary;
    if (props.$completed) return props.theme.colors.successGreen;
    return props.theme.colors.grayMedium;
  }};
  max-width: 100px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 968px) {
    font-size: 12px;
    max-width: 90px;
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
    max-width: 70px;
    min-height: auto;
    line-height: 1.2;
    font-weight: ${props => props.$active ? '700' : '500'};
    opacity: ${props => props.$active ? '1' : '0.7'};
  }
`;

const steps = [
  { id: 1, label: 'Escolha do Plano' },
  { id: 2, label: 'Dados Pessoais' },
  { id: 3, label: 'Dependentes e Pets' },
  { id: 4, label: 'Endereço' },
  { id: 5, label: 'Confirmar' }
];

const Stepper = ({ currentStep, onStepClick, completedSteps = [] }) => {
  const progress = ((currentStep - 1) / (steps.length - 1)) * 100;
  const stepsWrapperRef = useRef(null);
  const activeStepRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Detectar se está em mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const canNavigateToStep = (stepId) => {
    return stepId <= currentStep || completedSteps.includes(stepId);
  };
  
  // Scroll para o step ativo em mobile
  useEffect(() => {
    if (activeStepRef.current && stepsWrapperRef.current && isMobile) {
      const wrapper = stepsWrapperRef.current;
      const activeStep = activeStepRef.current;
      const scrollLeft = activeStep.offsetLeft - (wrapper.offsetWidth / 2) + (activeStep.offsetWidth / 2);
      
      wrapper.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }, [currentStep, isMobile]);
  
  // Em mobile, não mostrar o stepper no primeiro passo
  if (isMobile && currentStep === 1) {
    return null;
  }
  
  return (
    <StepperContainer>
      <StepsWrapper ref={stepsWrapperRef}>
        <ProgressLine>
          <ProgressLineFilled $progress={progress} />
        </ProgressLine>
        
        {steps.map((step) => (
          <StepItem
            key={step.id}
            ref={currentStep === step.id ? activeStepRef : null}
            $active={currentStep === step.id}
            $clickable={canNavigateToStep(step.id)}
            onClick={() => canNavigateToStep(step.id) && onStepClick(step.id)}
          >
            <StepCircle
              $active={currentStep === step.id}
              $completed={completedSteps.includes(step.id)}
            >
              {completedSteps.includes(step.id) ? <LuCheck size={20} /> : step.id}
            </StepCircle>
            <StepLabel
              $active={currentStep === step.id}
              $completed={completedSteps.includes(step.id)}
            >
              {step.label}
            </StepLabel>
          </StepItem>
        ))}
      </StepsWrapper>
    </StepperContainer>
  );
};

export default Stepper;

